/**
 * Iter 62 — First-time onboarding card.
 *
 * Renders a 6-option starter grid for brand-new users. Each tap
 * auto-sends the option label as the first chat turn — the player
 * never has to type. Skipped entirely for returning users (the
 * `/api/web/onboarding/status` endpoint enforces this server-side).
 *
 * The component is intentionally tiny: no tutorial, no walkthrough,
 * no carousel — just remove the blank-page problem.
 *
 * Props:
 *   options      — array from /onboarding/status `{id, label, icon}`
 *   onSelect     — (option) => void; fires when a chip is tapped
 *   disabled     — boolean; greys out the chips during in-flight ops
 */
import React from "react";
import { Target, Video, Frown, HelpCircle } from "lucide-react";

const ICON_MAP = {
  // lucide-react ships no "cricket" icon; emoji fallback handles it.
  cricket:  null,
  target:   Target,
  video:    Video,
  frown:    Frown,
  question: HelpCircle,
};

const ICON_EMOJI = {
  cricket:  "🏏",
  target:   "🎯",
  video:    "🎥",
  frown:    "😞",
  question: "🤔",
};

function OnboardingChip({ option, onSelect, disabled }) {
  const Icon = ICON_MAP[option.icon] || null;
  return (
    <button
      key={option.id}
      onClick={() => onSelect(option)}
      disabled={disabled}
      data-testid={`onboarding-option-${option.id}`}
      className="
        flex items-center gap-2.5 w-full text-left
        px-4 py-3 rounded-2xl
        bg-neutral-900/80 border border-neutral-800
        hover:bg-neutral-800 hover:border-neutral-700
        active:scale-[0.985]
        disabled:opacity-40 disabled:cursor-not-allowed
        transition-all
      "
    >
      <span className="text-lg leading-none w-6 text-center" aria-hidden="true">
        {Icon ? <Icon size={18} className="text-emerald-300/90 inline-block" /> :
          ICON_EMOJI[option.icon] || "•"}
      </span>
      <span className="text-sm text-neutral-100">{option.label}</span>
    </button>
  );
}

export default function OnboardingCard({ options, onSelect, disabled }) {
  if (!Array.isArray(options) || options.length === 0) return null;
  return (
    <div
      className="max-w-2xl mx-auto px-3 pt-3 pb-2"
      data-testid="onboarding-card"
    >
      <div className="mb-3 px-1">
        <div className="text-xl font-semibold text-neutral-50">
          👋 Welcome to Gully
        </div>
        <div className="text-sm text-neutral-400 mt-0.5">
          Not sure where to start? Just choose one below.
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {options.map((opt) => (
          <OnboardingChip
            key={opt.id}
            option={opt}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
